import { useCallback, useMemo, useState, MouseEvent } from 'react';
import { Box, IconButton, Avatar, styled } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@apollo/client';
import { theme } from '~/shared/styles/theme';
import { QuoidenLogo } from '~/assets/logos';
import { ME } from '~/graphql/queries';
import { ROUTES } from '~/shared/constants';
import { EXTERNAL_URLS } from 'common';
import ProfileMenu from './ProfileMenu';
import { handleLogout as handleLogoutUtil } from '~/shared/utils/auth';
import { GeneticSex } from '~/graphql/gen/graphql';

const AppHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth0();
  const { data: meData } = useQuery(ME);
  const [profileMenuAnchor, setProfileMenuAnchor] =
    useState<null | HTMLElement>(null);

  const me = meData?.me;
  const isAdmin = me?.isAdmin ?? false;

  // Compute user initials from firstName/lastName or email
  const userInitials = (() => {
    if (me?.firstName && me?.lastName) {
      return `${me.firstName[0]}${me.lastName[0]}`.toUpperCase();
    }
    if (me?.firstName) {
      return me.firstName.substring(0, 2).toUpperCase();
    }
    if (me?.email) {
      return me.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  })();

  // Get the current user's genetic sex from their linked person
  const userGeneticSex = useMemo(() => {
    if (!me?.ownedTree?.persons) {
      return null;
    }

    const userPerson = me.ownedTree.persons.find(
      (person) => person?.userId === me.id,
    );

    return userPerson?.geneticSex ?? null;
  }, [me?.id, me?.ownedTree?.persons]);

  // Get border color based on genetic sex
  const avatarBorderColor = useMemo(() => {
    if (userGeneticSex === GeneticSex.Female) {
      return theme.palette.custom.femaleProfileBorder;
    }
    if (userGeneticSex === GeneticSex.Male) {
      return theme.palette.custom.maleProfileBorder;
    }
    return undefined;
  }, [userGeneticSex]);

  const handleProfileMenuOpen = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      setProfileMenuAnchor(event.currentTarget);
    },
    [],
  );

  const handleProfileMenuClose = useCallback(() => {
    setProfileMenuAnchor(null);
  }, []);

  const handleLogout = useCallback(() => {
    handleLogoutUtil(logout);
  }, [logout]);

  const handleVaultNavigation = useCallback(() => {
    handleProfileMenuClose();
    navigate(ROUTES.VAULT);
  }, [navigate, handleProfileMenuClose]);

  const handleFamilyTreeNavigation = useCallback(() => {
    handleProfileMenuClose();
    navigate(ROUTES.TREE);
  }, [navigate, handleProfileMenuClose]);

  const handleLearnAboutOtc = useCallback(() => {
    handleProfileMenuClose();
    window.open(EXTERNAL_URLS.ABOUT_OTC_DEFICIENCY, '_blank');
  }, [handleProfileMenuClose]);

  return (
    <HeaderContainer>
      <LogoContainer>
        <QuoidenLogo width={150} height={48} />
      </LogoContainer>

      {me && (
        <>
          <StyledIconButton
            onClick={handleProfileMenuOpen}
            aria-haspopup="true"
            aria-expanded={Boolean(profileMenuAnchor)}
            aria-label="Open profile menu"
          >
            <StyledAvatar
              src={me.profileImageUrl ?? undefined}
              borderColor={avatarBorderColor}
            >
              {userInitials}
            </StyledAvatar>
          </StyledIconButton>

          <ProfileMenu
            anchorEl={profileMenuAnchor}
            open={Boolean(profileMenuAnchor)}
            onClose={handleProfileMenuClose}
            onFamilyTreeClick={handleFamilyTreeNavigation}
            onVaultClick={handleVaultNavigation}
            onLogoutClick={handleLogout}
            onLearnAboutOtcClick={handleLearnAboutOtc}
            isAdmin={isAdmin}
            currentPath={location.pathname}
          />
        </>
      )}
    </HeaderContainer>
  );
};

export default AppHeader;

const HeaderContainer = styled(Box)({
  width: '100%',
  height: `${theme.tokens.layout.headerHeight}px`,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingLeft: '80px',
  paddingRight: '72px',
  borderBottom: `solid 1px ${theme.palette.custom.green}`,
  backgroundColor: theme.palette.common.white,
});

const LogoContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const StyledIconButton = styled(IconButton)({
  padding: 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

const StyledAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== 'borderColor',
})<{ borderColor?: string }>(({ borderColor }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  width: 40,
  height: 40,
  fontWeight: 500,
  border: borderColor ? `2px solid ${borderColor}` : undefined,
  '& img': {
    objectFit: 'cover',
  },
}));
